import React, { useEffect, useMemo, useState } from 'react';
import ForecastChart, { CASE_STYLE } from './ForecastChart.jsx';
import { RRC_PUBLIC_SOURCE } from '../lib/leaseProduction.js';
import {
  CASES_DIFFER_NOTE,
  caseNumbersForAssistant,
  fmtInt,
  fmtMM,
  fmtMonth,
  fmtPct,
  forecastEffect,
  leaseLabel,
  packageFacts,
  valueCases,
} from './model.js';
import { Loading, NextStep, useAssistantSummary, useMeeting } from './common.jsx';

function priorPeak(recent, troughValue) {
  const idx = recent.findIndex((r) => Math.abs(r.mcfpd - troughValue) < 0.5);
  if (idx <= 0) return null;
  let best = null;
  recent.slice(0, idx).forEach((r, i) => {
    if (!best || r.mcfpd > best.mcfpd) best = { ...r, i };
  });
  if (!best) return null;
  return { ...best, troughMonth: recent[idx].month, months: idx - best.i };
}

const LEASE_COLORS = ['#0C1F2E', '#C77700', '#1F7A4D', '#6E8495'];

/** Monthly lease volumes (bbl or Mcf) when the package has no well-level actuals. */
function LeaseHistoryChart({ history }) {
  const [product, setProduct] = useState('oil');
  const leases = history?.leases || [];
  const valueKey = product === 'oil' ? 'oilBbl' : 'gasMcf';
  const unit = product === 'oil' ? 'bbl/month' : 'Mcf/month';
  const model = useMemo(() => {
    const tOf = (ym) => {
      const [y, m] = ym.split('-').map(Number);
      return y + (m - 0.5) / 12;
    };
    const hist = leases.flatMap((lease) =>
      (lease.monthly || [])
        .map((p) => ({ month: p.month, v: p[valueKey] }))
        .filter((p) => p.v > 0 && /^\d{4}-\d{2}$/.test(p.month)),
    );
    const forecastPts = leases.flatMap((lease) =>
      (lease.forecast || [])
        .map((p) => ({ month: p.month, v: p[valueKey] }))
        .filter((p) => p.v > 0 && /^\d{4}-\d{2}$/.test(p.month)),
    );
    if (!hist.length && !forecastPts.length) return null;
    const histTs = hist.map((p) => tOf(p.month));
    let t0 = histTs.length ? Math.min(...histTs) : Math.min(...forecastPts.map((p) => tOf(p.month)));
    let t1 = histTs.length ? Math.max(...histTs) : t0;
    // Keep recent history readable next to the start of the seller's forecast case.
    if (forecastPts.length) {
      const f0 = Math.min(...forecastPts.map((p) => tOf(p.month)));
      t0 = Math.max(t0, f0 - 8);
      t1 = Math.max(t1, Math.min(Math.max(...forecastPts.map((p) => tOf(p.month))), f0 + 5));
    }
    const visible = (p) => {
      const t = tOf(p.month);
      return t >= t0 - 0.01 && t <= t1 + 0.01;
    };
    const shown = [...hist, ...forecastPts].filter(visible);
    if (!shown.length) return null;
    const vMax = Math.max(...shown.map((p) => p.v));
    const W = 680;
    const H = 280;
    const M = { l: 52, r: 16, t: 16, b: 28 };
    const x = (t) => M.l + ((t - t0) / Math.max(t1 - t0, 1 / 12)) * (W - M.l - M.r);
    const y = (v) => M.t + (1 - v / (vMax * 1.08)) * (H - M.t - M.b);
    const pathOf = (points) =>
      points
        .filter(visible)
        .map((p, n) => `${n ? 'L' : 'M'}${x(tOf(p.month)).toFixed(1)},${y(p.v).toFixed(1)}`)
        .join(' ');
    const series = leases.map((lease, i) => {
      const line = (lease.monthly || [])
        .filter((p) => p[valueKey] > 0)
        .map((p) => ({ month: p.month, v: p[valueKey] }));
      const forecast = (lease.forecast || [])
        .filter((p) => p[valueKey] > 0)
        .map((p) => ({ month: p.month, v: p[valueKey] }));
      return {
        id: lease.id,
        name: lease.name,
        color: LEASE_COLORS[i % LEASE_COLORS.length],
        d: pathOf(line),
        forecast: pathOf(forecast),
      };
    });
    const years = [];
    const step = t1 - t0 > 15 ? 5 : 1;
    for (let yr = Math.ceil(t0); yr <= t1; yr += step) years.push(yr);
    return { W, H, M, series, years, x, y, vMax, hasForecast: forecastPts.length > 0 };
  }, [leases, valueKey]);
  const source = history?.source || RRC_PUBLIC_SOURCE;
  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">Forecast · lease-level history</span>
          <h2 className="mp-h2">
            {leases.length} {leases.length === 1 ? 'lease' : 'leases'}{' '}
            <small>· not wells</small>
          </h2>
        </div>
        <div className="mp-seg" role="group" aria-label="Product shown">
          <button type="button" aria-pressed={product === 'oil'} onClick={() => setProduct('oil')}>
            Oil
          </button>
          <button type="button" aria-pressed={product === 'gas'} onClick={() => setProduct('gas')}>
            Casinghead gas
          </button>
        </div>
      </div>
      <section className="mp-card" aria-label="Lease production history">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <h3 className="mp-h3">Lease-level {product === 'oil' ? 'oil' : 'casinghead gas'}, {unit}</h3>
          <span className="mp-note">Source: {source}</span>
        </div>
        <div className="mp-legend">
          {(model?.series || leases).map((lease, i) => (
            <span key={lease.id}>
              <i className="mp-sw" style={{ borderTopColor: lease.color || LEASE_COLORS[i % LEASE_COLORS.length] }} />
              <b>{lease.name}</b>
            </span>
          ))}
        </div>
        {model ? (
          <svg viewBox={`0 0 ${model.W} ${model.H}`} width="100%" role="img" aria-label="Lease-level production">
            {model.years.map((yr) => (
              <text key={yr} x={model.x(yr)} y={model.H - 8} textAnchor="middle" fontSize="11" fill="#5C6B78">
                {yr}
              </text>
            ))}
            <text x={8} y={model.M.t + 10} fontSize="11" fill="#5C6B78">
              {Math.round(model.vMax).toLocaleString()}
            </text>
            {model.series.map((s) => (
              <g key={s.id}>
                {s.d ? <path d={s.d} fill="none" stroke={s.color} strokeWidth="1.6" /> : null}
                {s.forecast ? (
                  <path d={s.forecast} fill="none" stroke={s.color} strokeWidth="1.6" strokeDasharray="5 4" />
                ) : null}
              </g>
            ))}
          </svg>
        ) : (
          <div className="mp-empty">No monthly {product} volumes in this file.</div>
        )}
        <p className="mp-note">
          Each line is one RRC lease.
          {model?.hasForecast
            ? " A dashed line is the seller's forecast case (gross) on that lease."
            : " The seller's forecast case is used for value until it is joined to these leases."}
        </p>
      </section>
    </div>
  );
}

function blankDraft(base) {
  return {
    qi: base ? String(Math.round(base.qi)) : '',
    declinePct: base ? base.declinePct.toFixed(2) : '',
    fadeTo: base?.fadeTo ? String(Math.round(base.fadeTo)) : '',
    fadeMonths: base?.fadeMonths ? String(base.fadeMonths) : '',
  };
}

export default function Forecast({ dealId, dealPackage, dealPackageFailed, onNavigate }) {
  const { desk, patchDesk, forecast } = useMeeting({ dealId, dealPackage });
  const [show, setShow] = useState('all');
  const [draft, setDraft] = useState(() => blankDraft(null));
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!forecast) return;
    const src = desk.custom ? forecast.cases.custom : forecast.cases.base;
    setDraft(blankDraft(src));
    // reset the editor when the deal or saved case changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, Boolean(forecast), desk.custom]);

  const facts = useMemo(() => (dealPackage ? packageFacts(dealPackage) : null), [dealPackage]);
  const values = useMemo(
    () => (dealPackage && forecast ? valueCases(dealPackage, forecast, desk) : []),
    [dealPackage, forecast, desk],
  );
  const vById = Object.fromEntries(values.map((v) => [v.id, v]));

  useAssistantSummary(
    forecast
      ? {
          surface: 'forecast',
          bidPriceMM: desk.bidPriceMM,
          bidEdited: desk.bidEdited === true,
          well: forecast.wellName,
          cases: Object.values(forecast.cases).map((c) => ({
            case: c.label,
            startMcfd: Math.round(c.qi),
            declinePctPerYr: +c.declinePct.toFixed(2),
            fadeTo: c.fadeTo,
            boswellVolAdj: c.volAdj,
          })),
          fit: forecast.fit,
          acid: forecast.acid,
          // Exact "Effect on PDP PV10" + per-case values as shown, so the gap is quoted not estimated.
          effectOnPdpPv10: forecastEffect(values),
          caseValues: caseNumbersForAssistant(values),
          casesDiffer: CASES_DIFFER_NOTE,
        }
      : null,
  );

  if (!dealPackage) {
    return dealPackageFailed ? (
      <div className="mp">
        <div className="mp-empty">No deal package is loaded for this deal on this host yet.</div>
      </div>
    ) : (
      <Loading label="Loading production history…" />
    );
  }
  if (!forecast) {
    if (dealPackage.productionHistory?.grain === 'lease') {
      return <LeaseHistoryChart history={dealPackage.productionHistory} />;
    }
    return (
      <div className="mp">
        <div className="mp-empty">
          No production history is loaded for this package's largest well yet, so there's no
          independent forecast to compare. The seller's case is used everywhere until actuals are
          loaded.
        </div>
      </div>
    );
  }

  const { cases, fit, acid, diagnostics, history } = forecast;
  const rateUnit = forecast.rateUnit || 'Mcf/d';
  const oilForecast = forecast.product === 'oil' || /bbl/i.test(rateUnit);
  const seller = cases.seller;
  const base = cases.base;
  const share = facts?.topShare;
  const peak = priorPeak(history.recent, acid.trough);
  const outliers = (dealPackage.assets || []).filter((a) => Math.abs((a.cfScale ?? 1) - 1) > 0.3).length;
  const nextWells = [...(dealPackage.assets || [])]
    .filter((a) => a.propnum !== forecast.asset.propnum)
    .sort((a, b) => (b.ref?.pv10 || 0) - (a.ref?.pv10 || 0))
    .slice(0, 2);
  const baseVsSeller =
    vById.base && vById.seller ? vById.base.pv10MM - vById.seller.pv10MM : null;
  const lastActual = history.recent[history.recent.length - 1];

  function saveCase() {
    const qi = Number(String(draft.qi).replace(/,/g, ''));
    const de = Number(draft.declinePct);
    if (!(qi > 0) || !(de > 0 && de < 80)) {
      setMsg('Enter a starting rate above 0 and a decline between 0 and 80% a year.');
      return;
    }
    const fadeTo = Number(String(draft.fadeTo).replace(/,/g, ''));
    patchDesk({
      custom: {
        label: 'Your case',
        qi,
        declinePct: de,
        fadeTo: fadeTo > 0 ? fadeTo : null,
        fadeMonths: fadeTo > 0 ? Number(draft.fadeMonths) || acid.fadeMonths : null,
      },
    });
    setMsg('Saved as "Your case". It now shows on the chart and as a column on Value.');
  }

  const paramRows = [
    {
      label: `Starting rate qi (${forecast.rateUnit || 'Mcf/d'})`,
      seller: fmtInt(seller.qi),
      base: fmtInt(base.qi),
      field: 'qi',
    },
    {
      label: 'Decline (%/yr, effective)',
      seller: seller.declinePct.toFixed(2),
      base: base.declinePct.toFixed(2),
      field: 'declinePct',
    },
    {
      label: 'b-factor',
      seller: '0 (exponential)',
      base: `0 (best hyperbolic ${fit.hyperbolic?.b ?? '—'})`,
      field: null,
    },
    {
      label: `Early fade to (${forecast.rateUnit || 'Mcf/d'})`,
      seller: '—',
      base: base.fadeTo ? fmtInt(base.fadeTo) : '—',
      field: 'fadeTo',
      placeholder: 'none',
    },
    {
      label: 'Fade period (months)',
      seller: '—',
      base: base.fadeMonths ? String(base.fadeMonths) : '—',
      field: 'fadeMonths',
      placeholder: '—',
    },
  ];

  return (
    <div className="mp">
      <div className="mp-head">
        <div>
          <span className="mp-eyebrow">Forecast · the well that drives the value</span>
          <h2 className="mp-h2">
            {forecast.wellName}{' '}
            <small>· {share != null ? `${Math.round(share * 100)}% seller-basis share of PDP PV10` : 'largest well'}</small>
          </h2>
        </div>
        <div className="mp-seg" role="group" aria-label="Cases shown">
          <button type="button" aria-pressed={show === 'all'} onClick={() => setShow('all')}>
            All cases
          </button>
          <button
            type="button"
            aria-pressed={show === 'seller-vs-ours'}
            onClick={() => setShow('seller-vs-ours')}
          >
            Seller vs. ours
          </button>
        </div>
      </div>

      <div className="mp-row">
        <section className="mp-card mp-grow-3" aria-label="Rate versus time">
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', alignItems: 'baseline' }}>
            <h3 className="mp-h3">
              {oilForecast ? 'Oil' : 'Gas'} rate, {rateUnit} (log scale): what it did, and where each case says it goes
            </h3>
            <span className="mp-note">
              annual averages · monthly since {fmtMonth(history.recent[0]?.month)}
            </span>
          </div>
          <div className="mp-legend">
            <span>
              <i className="mp-sw dot" style={{ background: '#0C1F2E' }} />
              <b>Actual</b>
            </span>
            <span>
              <i className="mp-sw" style={{ borderTopColor: CASE_STYLE.seller.stroke, borderTopStyle: 'dashed' }} />
              <b>Seller</b>&nbsp;(seller's forecast case)
            </span>
            <span>
              <i className="mp-sw" style={{ borderTopColor: CASE_STYLE.base.stroke }} />
              <b>Our base</b>&nbsp;(fit to actuals)
            </span>
            {show === 'all' ? (
              <span>
                <i className="mp-sw band" />
                Conservative ↔ aggressive
              </span>
            ) : null}
            {cases.custom && show === 'all' ? (
              <span>
                <i className="mp-sw" style={{ borderTopColor: CASE_STYLE.custom.stroke, borderTopStyle: 'dotted' }} />
                <b>Your case</b>
              </span>
            ) : null}
          </div>
          <ForecastChart forecast={forecast} show={show} />
          <div className="mp-tiles">
            <div className="mp-tile">
              <span className="k">Flowing-day uptime</span>
              <span className="v">{diagnostics.uptimePct != null ? fmtPct(diagnostics.uptimePct, 0) : '—'}</span>
            </div>
            <div className="mp-tile">
              <span className="k">Flowing tubing pressure</span>
              <span className="v">{diagnostics.latestFtp != null ? `${fmtInt(diagnostics.latestFtp)} psi` : '—'}</span>
            </div>
            <div className="mp-tile">
              <span className="k">Water–gas ratio</span>
              <span className="v">{diagnostics.wgr != null ? `${diagnostics.wgr.toFixed(1)}` : '—'}</span>
              <span className="s">bbl/MMcf</span>
            </div>
            <div className={`mp-tile ${acid.shortWindow ? 'warn' : ''}`}>
              <span className="k">Post-acid history</span>
              <span className="v">{acid.postAcidDays != null ? `${acid.postAcidDays} days` : '—'}</span>
              {acid.shortWindow ? <span className="s">too short to prove it lasts</span> : null}
            </div>
          </div>
        </section>

        <section className="mp-why mp-grow-2" aria-label="Why our forecast differs">
          <div className="eyebrow">WHY OUR FORECAST DIFFERS</div>
          <p className="lead">
            {oilForecast
              ? `The seller starts ${forecast.wellName} at ${fmtInt(seller.qi)} ${rateUnit} and declines it ${seller.declinePct.toFixed(1)}% a year. Our base fades the recent high rate toward the observed trough.`
              : `The seller starts ${forecast.wellName} at ${fmtInt(seller.qi)} Mcf/d, its post-acid rate, and declines it ${seller.declinePct.toFixed(1)}% a year. Our base assumes the uplift fades the way it did last time.`}
          </p>
          <ol>
            <li>
              <span>1</span>
              <span>
                <b>{oilForecast ? 'The recent high rate is unproven.' : 'The acid uplift is unproven.'}</b>{' '}
                Rates are back near {fmtInt(acid.postAcid)} {rateUnit},
                but only {acid.postAcidDays ?? '—'} days of data follow {oilForecast ? 'that high' : 'the job'}.
                {peak
                  ? ` The last time rates were this high (${fmtInt(peak.mcfpd)} in ${fmtMonth(peak.month)}), they fell to ${fmtInt(acid.trough)} within ${peak.months} months.`
                  : ''}
              </span>
            </li>
            <li>
              <span>2</span>
              <span>
                <b>The decline is steeper in the data.</b> Fit to {fit.window} annual actuals:{' '}
                {fit.declinePct.toFixed(1)}%/yr vs. the seller's {seller.declinePct.toFixed(1)}%.
                {fit.holdoutMapePct != null
                  ? ` Held-back years are predicted within ${fit.holdoutMapePct}% on average.`
                  : ''}
              </span>
            </li>
            <li>
              <span>3</span>
              <span>
                <b>No sign of flattening.</b> The best hyperbolic fit has b ={' '}
                {fit.hyperbolic?.b ?? '—'}
                {fit.hyperbolic?.r2 != null ? ` (R² ${fit.hyperbolic.r2.toFixed(2)})` : ''}, effectively
                exponential, so no case leans on a flattening tail.
              </span>
            </li>
          </ol>
          <div className="foot">
            <span>Effect on PDP PV10</span>
            <span>
              {baseVsSeller != null
                ? `Our base is ${fmtMM(Math.abs(baseVsSeller), 2)} ${baseVsSeller < 0 ? 'below' : 'above'} the seller's case`
                : '—'}
            </span>
          </div>
        </section>
      </div>

      <section className="mp-card" aria-label="Decline parameters">
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', alignItems: 'baseline' }}>
          <h3 className="mp-h3">Reshape the decline: your case</h3>
          <span className="mp-note">Saved as a named case next to the seller's, never over it. Flows straight into Value.</span>
        </div>
        <div className="mp-table-wrap">
          <table className="mp-table" style={{ minWidth: 640 }}>
            <thead>
              <tr>
                <th>Parameter</th>
                <th>Seller</th>
                <th>Our base</th>
                <th>Your case</th>
              </tr>
            </thead>
            <tbody>
              {paramRows.map((r) => (
                <tr key={r.label}>
                  <td>{r.label}</td>
                  <td className="mp-num">{r.seller}</td>
                  <td className="mp-num">{r.base}</td>
                  <td style={{ paddingTop: 6, paddingBottom: 6 }}>
                    {r.field ? (
                      <input
                        className="mp-param-input"
                        aria-label={`Your case: ${r.label}`}
                        inputMode="decimal"
                        value={draft[r.field]}
                        placeholder={r.placeholder || ''}
                        onChange={(e) => {
                          setDraft((d) => ({ ...d, [r.field]: e.target.value }));
                          setMsg(null);
                        }}
                      />
                    ) : (
                      <span className="mp-num dim">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" className="mp-btn" onClick={saveCase}>
            Save as case
          </button>
          {desk.custom ? (
            <button
              type="button"
              className="mp-btn ghost"
              onClick={() => {
                patchDesk({ custom: null });
                setMsg('Your case removed.');
              }}
            >
              Remove your case
            </button>
          ) : null}
          <button
            type="button"
            className="mp-btn ghost"
            aria-pressed={desk.forecastApproved}
            onClick={() => patchDesk({ forecastApproved: !desk.forecastApproved })}
          >
            {desk.forecastApproved ? '✓ Our base approved' : 'Approve our base'}
          </button>
          {msg ? <span className="mp-note" role="status">{msg}</span> : null}
          <span className="mp-note" style={{ marginLeft: 'auto' }}>
            Seller's forecast case · Ours: fit on actuals through {lastActual ? fmtMonth(lastActual.month) : '—'}
          </span>
        </div>
      </section>

      <div className="mp-row">
        <section className="mp-card mp-grow-3" aria-label="Review queue">
          <h3 className="mp-h3">Review queue</h3>
          <p className="mp-p">
            <b>{outliers} wells</b> where our rebuild doesn't reconcile to the seller's oneline (scale factor
            off by more than 30%). Review these before relying on well-level numbers.
          </p>
          {nextWells.length ? (
            <p className="mp-p">
              Next by value:{' '}
              {nextWells.map((a, i) => (
                <span key={a.propnum}>
                  {i ? ' · ' : ''}
                  <button
                    type="button"
                    className="mp-link"
                    onClick={() => onNavigate(`well/${encodeURIComponent(a.propnum)}`)}
                  >
                    {leaseLabel(a.lease)}
                  </button>{' '}
                  ({fmtMM((a.ref?.pv10 || 0) / 1e6, 2)})
                </span>
              ))}
            </p>
          ) : null}
          <div>
            <button type="button" className="mp-link" onClick={() => onNavigate('assets')}>
              Open the multi-well decline workbench →
            </button>
          </div>
        </section>
        <div className="mp-grow-2">
          <NextStep
            label="3 · Value"
            question="What is it worth under each view, and what would we pay?"
            onClick={() => onNavigate('value')}
          />
        </div>
      </div>
    </div>
  );
}
